import { useEffect, useRef, useState } from "react";
import { Package, Trash2, Upload } from "lucide-react";

import { Field } from "@/components/cinch/Field";
import { NumberInput } from "@/components/cinch/NumberInput";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

import { useSaveItem, useUploadPhoto, type Item } from "../api";

/**
 * Edit a catalog item — and, mostly, add the photo nobody had to hand when the
 * item was first created.
 *
 * The photo is what makes the Summary tab usable ("which blanket?"), and it
 * almost never exists at creation time: someone adds the item the moment the
 * order is placed and the product shot turns up later. Without an edit door
 * that photo could never be attached.
 */
export function ItemDialog({
  item,
  open,
  onOpenChange,
}: {
  item: Item | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const save = useSaveItem();
  const upload = useUploadPhoto();
  const fileRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [cost, setCost] = useState<number | null>(null);
  const [threshold, setThreshold] = useState<number | null>(3);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [isActive, setIsActive] = useState(true);

  // Re-seed from the item each time it opens, so editing a second item never
  // shows the first one's values.
  useEffect(() => {
    if (!open || !item) return;
    setName(item.name);
    setDescription(item.description ?? "");
    setCost(item.default_unit_cost);
    setThreshold(item.low_stock_threshold);
    setPhotoUrl(item.photo_url);
    setIsActive(item.is_active);
    save.reset();
    upload.reset();
    // Only re-seed on open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item?.id]);

  if (!item) return null;

  const submit = () => {
    save.mutate(
      {
        id: item.id,
        body: {
          name: name.trim(),
          description: description || null,
          photo_url: photoUrl,
          default_unit_cost: cost,
          low_stock_threshold: threshold ?? 0,
          is_active: isActive,
          sort_order: item.sort_order,
        },
      },
      { onSuccess: () => onOpenChange(false) },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit {item.name}</DialogTitle>
          <DialogDescription>
            The kind of thing, not a specific order. Changing the name here
            renames it everywhere, including on giveaways already recorded.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-4">
            {photoUrl ? (
              <img
                src={photoUrl}
                alt={item.name}
                className="h-20 w-20 rounded-lg border object-cover"
              />
            ) : (
              <div className="flex h-20 w-20 items-center justify-center rounded-lg border bg-muted">
                <Package className="h-7 w-7 text-muted-foreground" />
              </div>
            )}
            <div className="flex flex-col gap-2">
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    upload.mutate(file, { onSuccess: (r) => setPhotoUrl(r.url) });
                  }
                }}
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileRef.current?.click()}
                disabled={upload.isPending}
              >
                <Upload className="mr-1 h-4 w-4" />
                {upload.isPending
                  ? "Uploading…"
                  : photoUrl
                    ? "Replace photo"
                    : "Add photo"}
              </Button>
              {photoUrl && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-danger hover:bg-danger-light hover:text-danger"
                  onClick={() => setPhotoUrl(null)}
                >
                  <Trash2 className="mr-1 h-4 w-4" /> Remove
                </Button>
              )}
            </div>
          </div>

          <Field
            id="edit-item-name"
            label="Item name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <NumberInput
              id="edit-item-cost"
              label="Typical cost"
              prefix="$"
              value={cost}
              onChange={setCost}
              helper="Used only until a real order gives us a price"
            />
            <NumberInput
              id="edit-item-threshold"
              label="Flag as low at"
              maxDecimals={0}
              value={threshold}
              onChange={setThreshold}
              helper="Summary shows a Low badge at or below this"
            />
          </div>
          <Textarea
            placeholder="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
          />

          <div className="flex items-center justify-between rounded-xl border p-3">
            <div>
              <Label htmlFor="edit-item-active">Available to request</Label>
              <p className="text-xs text-muted-foreground">
                Turning this off hides it from the request form. Everything
                already given away stays in the ledger.
              </p>
            </div>
            <Switch
              id="edit-item-active"
              checked={isActive}
              onCheckedChange={setIsActive}
            />
          </div>

          {upload.error && <Alert variant="danger">{String(upload.error)}</Alert>}
          {save.error && <Alert variant="danger">{String(save.error)}</Alert>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!name.trim() || save.isPending} onClick={submit}>
            {save.isPending ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
